import { getImage, IGatsbyImageData, ImageDataLike } from 'gatsby-plugin-image';
import { useMemo } from 'react';
import { css } from 'styled-components';
import {
  GeneralMediaAssetProps,
  MediaAssetProps,
  ViewMediaAssetProps,
} from '.';
import { ContentfulAsset } from '../../../features/entities/general-entities';
import styleToCss from '../../../utils/styleToCSS';
import { StyledAssetProps } from './styles';

type ASSET_MEDIA_QUERIES = 'default' | 'tablet' | 'xl' | 'lg';

export interface DefaultMediaAssetProps
  extends Omit<GeneralMediaAssetProps, 'asset'> {
  image: IGatsbyImageData | undefined;
  path: string | undefined;
  desc: string | undefined;
  width: number | undefined;
  height: number | undefined;
}
const useMediaAssetProps = (props: MediaAssetProps) => {
  return useMemo(() => {
    const assetProps: StyledAssetProps = {
      $default: {},
      $tablet: {},
      $xl: {},
      $lg: {},
    };

    const prepareProps = (
      data: ViewMediaAssetProps | undefined,
      mediaQuery: ASSET_MEDIA_QUERIES,
    ) => {
      if (!!data && Object.keys(data).length) {
        const { width, height, style } = data;

        if (!!style) {
          assetProps[`$${mediaQuery}`] = css`
            ${assetProps[`$${mediaQuery}` as keyof StyledAssetProps]};
            ${styleToCss(style)}
          `;
        }
        if (!!width) {
          assetProps[`$${mediaQuery}`] = css`
            ${assetProps[`$${mediaQuery}` as keyof StyledAssetProps]};
            width: ${width}px;
          `;
        }
        if (!!height) {
          assetProps[`$${mediaQuery}`] = css`
            ${assetProps[`$${mediaQuery}` as keyof StyledAssetProps]};
            height: ${height}px;
          `;
        }
      }
    };

    const {
      xl,
      xxl,
      tablet,
      children,
      loading = 'lazy',
      objectFit = 'cover',
      alt,

      id,
      className,
      onClick,
      asset,
      fallback,
      style,
      width,
      height,
      ...defaultViewProps
    } = props;

    const image = getImage(asset as ImageDataLike);
    const path = `${asset?.file?.url ?? ''}`;
    const desc = !!alt ? alt : ((asset as ContentfulAsset)?.title as string);

    const generalProps: DefaultMediaAssetProps = {
      children,
      loading,
      objectFit,
      alt,
      id,
      className,
      onClick,
      image,
      path,
      desc,
      fallback,
      style,
      width,
      height,
      ...defaultViewProps,
    };

    prepareProps({ width, height, style }, 'default');

    prepareProps(xxl, 'xl');
    prepareProps(xl, 'lg');
    prepareProps(tablet, 'tablet');

    return { assetProps, generalProps };
  }, [props]);
};
export default useMediaAssetProps;
